import React, { useMemo } from 'react';
import { TradingAccountItem, MappedOverallTradingAccountData } from './use-overall-trading-account';
import { createCurrencyFormatter } from '../../utils/globalCurrency';

interface TradingAccountRow {
  particular: string;
  amountCol1: number | undefined;
  amountCol2: number | undefined;
  operation: 'None' | 'Add' | 'Less' | 'Result' | 'Result(=)' | 'Result(-)';
}

interface TradingAccountPrintableProps {
  title: string;
  data: MappedOverallTradingAccountData | null;
  startDate?: string;
  endDate?: string;
  currencySymbol?: string;
}

// Create currency formatter with default $ symbol
const createFormatCurrency = (currencySymbol: string = '$') => {
  const formatter = createCurrencyFormatter(currencySymbol);
  return (amount: number) => {
    const isNegative = amount < 0;
    const formatted = formatter.format(Math.abs(amount), 2);
    return isNegative ? `-${formatted}` : formatted;
  };
};

const TradingAccountPrintable = React.forwardRef<HTMLDivElement, TradingAccountPrintableProps>(
  ({ title, data, startDate, endDate, currencySymbol = '$' }, ref) => {
    // Create currency formatter with provided symbol
    const formatCurrency = createFormatCurrency(currencySymbol);

    const cellStyle: React.CSSProperties = {
      padding: '8px 12px',
      fontSize: '13px',
      borderBottom: '1px solid #e5e7eb',
    };

    const headerCellStyle: React.CSSProperties = {
      ...cellStyle,
      fontWeight: 600,
      color: '#6b7280',
      borderBottom: '2px solid #d1d5db',
    };

    // Organize trading account data in proper format
    const organizedData = useMemo(() => {
      if (!data) return { debitRows: [], creditRows: [], totalDr: 0, totalCr: 0 };

      const { debitSide, creditSide } = data;

      // Helper function to get amount from items array
      const getAmount = (items: Array<{ name: string; amount: number }>, searchName: string): number => {
        const normalizedSearch = searchName.toLowerCase().trim();
        const item = items.find(item =>
          item.name.toLowerCase().trim() === normalizedSearch
        );
        return item?.amount || 0;
      };

      // Get values from debit side
      const openingStock = getAmount(debitSide.items, 'Opening Stock');
      const purchases = getAmount(debitSide.items, 'Purchases');
      const carriageInwards = getAmount(debitSide.items, 'Carriage Inwards');
      const returnOutwards = getAmount(debitSide.items, 'Return Outwards');
      const costOfGoodsAvailable = getAmount(debitSide.items, 'Cost of Goods Available');
      const closingStock = getAmount(debitSide.items, 'Closing Stock');
      const costOfGoodsSold = getAmount(debitSide.items, 'Cost of Goods Sold');
      const grossProfit = getAmount(debitSide.items, 'Gross Profit');
      const totalDr = debitSide.total || 0;

      // Get values from credit side
      const sales = getAmount(creditSide.items, 'Sales');
      const returnInwards = getAmount(creditSide.items, 'Return Inwards');
      const carriageOutwards = getAmount(creditSide.items, 'Carriage Outwards');
      const totalCr = creditSide.total || 0;

      // Debit side rows (8 rows)
      const debitRows: TradingAccountRow[] = [
        { particular: 'Opening Stock', amountCol1: openingStock, amountCol2: undefined, operation: 'None' },
        { particular: 'Purchases', amountCol1: purchases, amountCol2: undefined, operation: 'Add' },
        { particular: 'Carriage Inwards', amountCol1: carriageInwards, amountCol2: undefined, operation: 'Add' },
        { particular: 'Return Outwards', amountCol1: returnOutwards, amountCol2: undefined, operation: 'Less' },
        { particular: 'Cost of Goods Available', amountCol1: undefined, amountCol2: costOfGoodsAvailable, operation: 'Result(=)' },
        { particular: 'Closing Stock', amountCol1: undefined, amountCol2: closingStock, operation: 'Less' },
        { particular: 'Cost of Goods Sold', amountCol1: undefined, amountCol2: costOfGoodsSold, operation: 'Result(-)' },
        { particular: 'Gross Profit', amountCol1: undefined, amountCol2: grossProfit, operation: 'Result(-)' },
      ];

      // Credit side rows (8 rows)
      const creditRows: TradingAccountRow[] = [
        { particular: 'Sales', amountCol1: sales, amountCol2: undefined, operation: 'None' },
        { particular: 'Return Inwards', amountCol1: returnInwards, amountCol2: undefined, operation: 'Less' },
        { particular: 'Carriage Outwards', amountCol1: carriageOutwards, amountCol2: undefined, operation: 'Less' },
        { particular: '', amountCol1: undefined, amountCol2: undefined, operation: 'Result(-)' },
        // Add 4 empty rows
        { particular: '', amountCol1: undefined, amountCol2: undefined, operation: 'None' },
        { particular: '', amountCol1: undefined, amountCol2: undefined, operation: 'None' },
        { particular: '', amountCol1: undefined, amountCol2: undefined, operation: 'None' },
        { particular: '', amountCol1: undefined, amountCol2: undefined, operation: 'None' },
      ];

      return {
        debitRows,
        creditRows,
        totalDr,
        totalCr
      };
    }, [data]);

    const renderSide = (sideTitle: string, rows: TradingAccountRow[], total: number, isDebitSide: boolean) => (
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937', marginBottom: '8px' }}>
          {sideTitle}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...headerCellStyle, textAlign: 'left', width: '40%' }}></th>
              <th style={{ ...headerCellStyle, textAlign: 'right', width: '30%' }}></th>
              <th style={{ ...headerCellStyle, textAlign: 'right', width: '30%' }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={3} style={{ ...cellStyle, textAlign: 'center', color: '#6b7280' }}>
                  No data
                </td>
              </tr>
            ) : (
              rows.map((row, index) => {
                // Determine operation symbol
                let operationSymbol = '';
                if (row.operation === 'Add') operationSymbol = '+ ';
                if (row.operation === 'Less') operationSymbol = '- ';
                if (row.operation === 'Result(=)' || row.operation === 'Result(-)') operationSymbol = '= ';

                return (
                  <tr key={`${row.particular}-${index}`} style={{ height: '40px' }}>
                    <td style={{ ...cellStyle, verticalAlign: 'middle' }}>
                      {row.particular}
                    </td>
                    <td style={{ ...cellStyle, textAlign: 'right', verticalAlign: 'middle' }}>
                      {row.amountCol1 !== undefined ? `${operationSymbol}${formatCurrency(row.amountCol1)}` : ''}
                    </td>
                    <td style={{ ...cellStyle, textAlign: 'right', verticalAlign: 'middle' }}>
                      {row.amountCol2 !== undefined ? `${operationSymbol}${formatCurrency(row.amountCol2)}` : ''}
                    </td>
                  </tr>
                );
              })
            )}
            {/* Total row with divider */}
            <tr style={{ height: '40px' }}>
              <td style={{ ...cellStyle, borderTop: '1px solid #d1d5db', verticalAlign: 'middle' }}></td>
              <td style={{ ...cellStyle, textAlign: 'right', borderTop: '1px solid #d1d5db', fontWeight: 600, verticalAlign: 'middle' }}>
                {/* Total shows in column 2 for both sides */}
              </td>
              <td style={{ ...cellStyle, textAlign: 'right', borderTop: '1px solid #d1d5db', fontWeight: 600, verticalAlign: 'middle' }}>
                {`= ${formatCurrency(total)}`}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );

    return (
      <div
        ref={ref}
        style={{
          width: '800px',
          background: '#ffffff',
          fontFamily: 'Arial, Helvetica, sans-serif',
          color: '#1f2937',
          padding: '32px',
        }}
      >
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '18px', fontWeight: 700 }}>{title}</div>
          {(startDate || endDate) && (
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              {startDate && `From: ${startDate}`}
              {startDate && endDate && '  —  '}
              {endDate && `To: ${endDate}`}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '24px' }}>
          {renderSide('DR', organizedData.debitRows, organizedData.totalDr, true)}
          <div style={{ width: '1px', background: '#d1d5db' }}></div>
          {renderSide('CR', organizedData.creditRows, organizedData.totalCr, false)}
        </div>
      </div>
    );
  }
);

TradingAccountPrintable.displayName = 'TradingAccountPrintable';

export default TradingAccountPrintable;
